import { useMemo, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyIcon, ExclamationTriangleIcon, ClockIcon, UserPlusIcon, PlusIcon, ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchInventory } from '../store/keyInventorySlice';
import { fetchAssignments } from '../store/assignmentsSlice';
import { fetchLocations } from '../store/locationsSlice';
import { fetchKeyTypes } from '../store/keyTypesSlice';
import { fetchPeople } from '../store/peopleSlice';
import { fetchKeyRequests } from '../store/keyRequestsSlice';
import { notificationSettingsApi, visitorsApi } from '../services/api';
import { LowInventoryAlert, Visitor, KeyRequest, KeyInventoryItem } from '../types';
import Card from '../components/Card';

export default function Dashboard() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { inventory, loading: inventoryLoading } = useAppSelector((state) => state.keyInventory);
  const { assignments, loading: assignmentsLoading } = useAppSelector((state) => state.assignments);
  const { locations, currentLocationId, loading: locationsLoading } = useAppSelector((state) => state.locations);
  const { keyTypes, loading: keyTypesLoading } = useAppSelector((state) => state.keyTypes);
  const { people, loading: peopleLoading } = useAppSelector((state) => state.people);
  const { requests: keyRequests, loading: keyRequestsLoading } = useAppSelector((state) => state.keyRequests);
  const currentUser = useAppSelector((state) => state.users.currentUser);
  const users = useAppSelector((state) => state.users.users);
  
  const isAdmin = currentUser?.role === 'admin';
  const isSiteAdmin = currentUser?.role === 'site-admin';
  const canApproveRequests = isAdmin || isSiteAdmin;
  
  const [lowInventoryThreshold, setLowInventoryThreshold] = useState(5);
  const [showLowInventoryAlerts, setShowLowInventoryAlerts] = useState(true);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [visitorsLoading, setVisitorsLoading] = useState(false);

  // Fetch data on mount to ensure fresh data
  useEffect(() => {
    dispatch(fetchInventory());
    dispatch(fetchAssignments());
    dispatch(fetchLocations());
    dispatch(fetchKeyTypes());
    dispatch(fetchPeople());
    dispatch(fetchKeyRequests());
    
    // Fetch active visitors
    setVisitorsLoading(true);
    visitorsApi.getActive().then(data => {
      setVisitors(data);
    }).catch(err => {
      console.error('Failed to load visitors:', err);
    }).finally(() => {
      setVisitorsLoading(false);
    });
    
    // Fetch notification settings for threshold
    notificationSettingsApi.get().then(settings => {
      if (settings.lowInventoryThreshold !== undefined) {
        setLowInventoryThreshold(settings.lowInventoryThreshold);
      }
      if (settings.notifyOnLowInventory !== undefined) {
        setShowLowInventoryAlerts(settings.notifyOnLowInventory);
      }
    }).catch(err => {
      console.error('Failed to load notification settings:', err);
    });
  }, [dispatch]);

  const loading = inventoryLoading || assignmentsLoading || locationsLoading || keyTypesLoading || peopleLoading || visitorsLoading || keyRequestsLoading;

  // Calculate assigned quantities per inventory item
  const assignedByInventoryItem = useMemo(() => {
    const assigned = new Map<string, number>();
    assignments
      .filter(a => a.status === 'active')
      .forEach(a => {
        const current = assigned.get(a.keyInventoryItemId) || 0;
        assigned.set(a.keyInventoryItemId, current + a.quantity);
      });
    return assigned;
  }, [assignments]);

  // Get visitors with overdue checkout times (filtered by current location)
  const overdueVisitors = useMemo(() => {
    const now = new Date();
    return visitors
      .filter(v => v.status === 'checked-in' && v.expectedCheckOutTime)
      .filter(v => v.locationId === currentLocationId)
      .filter(v => new Date(v.expectedCheckOutTime!) < now)
      .sort((a, b) => new Date(a.expectedCheckOutTime!).getTime() - new Date(b.expectedCheckOutTime!).getTime());
  }, [visitors, currentLocationId]);

  // Get the last 10 active key assignments (filtered by current location), sorted by assignedAt date (most recent first)
  const recentAssignments = useMemo(() => {
    return [...assignments]
      .filter(a => a.status === 'active')
      .filter(a => {
        const inventoryItem = inventory.find(i => i.id === a.keyInventoryItemId);
        return inventoryItem?.locationId === currentLocationId;
      })
      .sort((a, b) => new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime())
      .slice(0, 10);
  }, [assignments, inventory, currentLocationId]);

  // Get pending key requests (filtered by accessible locations for site-admin)
  const pendingKeyRequests = useMemo(() => {
    if (!canApproveRequests) return [];
    
    const pending = keyRequests.filter(r => r.status === 'pending');
    
    if (isAdmin) {
      // Admins see all pending requests
      return pending;
    }
    
    if (isSiteAdmin && currentUser?.locationAccess) {
      // Site-admins see requests for their accessible locations
      return pending.filter(request => {
        const invItem = inventory.find((i: KeyInventoryItem) => i.id === request.keyInventoryItemId);
        return invItem && currentUser.locationAccess.includes(invItem.locationId);
      });
    }
    
    return [];
  }, [keyRequests, canApproveRequests, isAdmin, isSiteAdmin, currentUser, inventory]);

  // Calculate low inventory alerts (filtered by current location)
  const lowInventoryAlerts = useMemo((): LowInventoryAlert[] => {
    if (!showLowInventoryAlerts) return [];
    
    const alerts: LowInventoryAlert[] = [];
    
    inventory
      .filter(item => item.locationId === currentLocationId)
      .forEach(item => {
        const assigned = assignedByInventoryItem.get(item.id) || 0;
        const available = item.quantity - assigned;
        
        if (available <= lowInventoryThreshold) {
          const keyType = keyTypes.find(kt => kt.id === item.keyTypeId);
          const location = locations.find(l => l.id === item.locationId);
          
          if (keyType && location) {
            alerts.push({
              keyTypeId: item.keyTypeId,
              keyTypeName: keyType.name,
              locationId: item.locationId,
              locationName: location.name,
              available,
              threshold: lowInventoryThreshold,
              inventoryItemId: item.id,
            });
          }
        }
      });
    
    // Sort by available (lowest first)
    return alerts.sort((a, b) => a.available - b.available);
  }, [inventory, keyTypes, locations, assignedByInventoryItem, lowInventoryThreshold, showLowInventoryAlerts, currentLocationId]);

  if (loading && inventory.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Dashboard</h1>
          <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
            Overview of your key inventory system
          </p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-secondary-600 dark:text-secondary-400">Loading dashboard data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">Dashboard</h1>
        <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-400">
          Overview of your key inventory system
        </p>
      </div>

      {/* Pending Key Requests */}
      {canApproveRequests && pendingKeyRequests.length > 0 && (
        <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-900/10">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <ClipboardDocumentListIcon className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                Pending Key Requests
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
                {pendingKeyRequests.length} request{pendingKeyRequests.length !== 1 ? 's' : ''} awaiting your review:
              </p>
              <div className="space-y-2">
                {pendingKeyRequests.slice(0, 5).map((request: KeyRequest) => {
                  const person = people.find(p => p.id === request.personId);
                  const invItem = inventory.find((i: KeyInventoryItem) => i.id === request.keyInventoryItemId);
                  const keyType = keyTypes.find(kt => kt.id === invItem?.keyTypeId);
                  const location = locations.find(l => l.id === invItem?.locationId);
                  const requester = users.find(u => u.id === request.requestedBy);

                  return (
                    <div
                      key={request.id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-secondary-900 dark:text-secondary-100 truncate">
                            {keyType?.name || 'Unknown Key'}
                          </span>
                          {request.quantity > 1 && (
                            <span className="text-secondary-500 dark:text-secondary-400 text-sm">
                              x{request.quantity}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                          For {person?.name || 'Unknown'} at {location?.name || 'Unknown Location'}
                        </p>
                        <p className="text-xs text-secondary-400 dark:text-secondary-500">
                          Requested by {requester?.name || 'Unknown'} • {new Date(request.requestedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Link
                        to="/key-requests"
                        className="ml-2 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                      >
                        Review
                      </Link>
                    </div>
                  );
                })}
                {pendingKeyRequests.length > 5 && (
                  <Link
                    to="/key-requests"
                    className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    View all {pendingKeyRequests.length} pending requests →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4">
          Quick Actions
        </h3>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => navigate('/people?action=assign')}
            className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            <KeyIcon className="w-5 h-5 mr-2" />
            Assign a Key
          </button>
          <button
            onClick={() => navigate('/people?action=return')}
            className="inline-flex items-center px-4 py-2 bg-secondary-600 hover:bg-secondary-700 text-white rounded-lg font-medium transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5 mr-2" />
            Return a Key
          </button>
          <button
            onClick={() => navigate('/people?action=add')}
            className="inline-flex items-center px-4 py-2 bg-success hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <UserPlusIcon className="w-5 h-5 mr-2" />
            Add Person
          </button>
          <button
            onClick={() => navigate('/visitors?action=checkin')}
            className="inline-flex items-center px-4 py-2 bg-info hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Check In Visitor
          </button>
        </div>
      </Card>

      {/* Overdue Visitor Keys Alert */}
      {overdueVisitors.length > 0 && (
        <Card className="border-red-500/50 bg-red-50 dark:bg-red-900/10">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <ClockIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                Overdue Visitor Keys
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
                The following visitors have not returned their keys by their expected checkout time:
              </p>
              <div className="space-y-2">
                {overdueVisitors.slice(0, 5).map((visitor) => {
                  const location = locations.find(l => l.id === visitor.locationId);
                  const overdueTime = new Date(visitor.expectedCheckOutTime!);
                  const hoursOverdue = Math.round((Date.now() - overdueTime.getTime()) / (1000 * 60 * 60));
                  
                  return (
                    <div
                      key={visitor.id}
                      className="flex items-center justify-between p-2 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700"
                    >
                      <div>
                        <span className="font-medium text-secondary-900 dark:text-secondary-100">
                          {visitor.name}
                        </span>
                        {visitor.company && (
                          <span className="text-secondary-500 dark:text-secondary-400 text-sm ml-2">
                            ({visitor.company})
                          </span>
                        )}
                        <span className="text-secondary-500 dark:text-secondary-400 text-sm ml-2">
                          at {location?.name || 'Unknown Location'}
                        </span>
                      </div>
                      <span className="px-2 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                        {hoursOverdue > 0 ? `${hoursOverdue}h overdue` : 'Overdue'}
                      </span>
                    </div>
                  );
                })}
                {overdueVisitors.length > 5 && (
                  <Link
                    to="/visitors"
                    className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    View all {overdueVisitors.length} overdue visitors →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Low Inventory Alerts */}
      {lowInventoryAlerts.length > 0 && (
        <Card className="border-warning/50 bg-warning/5 dark:bg-warning/10">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-warning/20 rounded-lg">
              <ExclamationTriangleIcon className="w-6 h-6 text-warning" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
                Low Inventory Alert
              </h3>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
                The following key types are running low (≤{lowInventoryThreshold} available):
              </p>
              <div className="space-y-2">
                {lowInventoryAlerts.slice(0, 5).map((alert) => (
                  <div
                    key={alert.inventoryItemId}
                    className="flex items-center justify-between p-2 bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700"
                  >
                    <div>
                      <span className="font-medium text-secondary-900 dark:text-secondary-100">
                        {alert.keyTypeName}
                      </span>
                      <span className="text-secondary-500 dark:text-secondary-400 text-sm ml-2">
                        at {alert.locationName}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                      alert.available === 0
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    }`}>
                      {alert.available} available
                    </span>
                  </div>
                ))}
                {lowInventoryAlerts.length > 5 && (
                  <Link
                    to="/inventory"
                    className="block text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    View all {lowInventoryAlerts.length} low inventory items →
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Recent Key Assignments */}
      <Card title="Recent Key Assignments">
          {recentAssignments.length > 0 ? (
            <div className="space-y-3">
              {recentAssignments.map((assignment) => {
                const person = people.find((p) => p.id === assignment.personId);
                const inventoryItem = inventory.find((i) => i.id === assignment.keyInventoryItemId);
                const keyType = keyTypes.find((kt) => kt.id === inventoryItem?.keyTypeId);
                const location = locations.find((l) => l.id === inventoryItem?.locationId);

                return (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between pb-3 border-b border-secondary-100 dark:border-secondary-700 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                        {person?.name || 'Unknown Person'}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        {keyType?.name || 'Unknown Key'} ({location?.name || 'Unknown Location'})
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                        x{assignment.quantity}
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        {new Date(assignment.assignedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-secondary-500 dark:text-secondary-400">
              No key assignments yet
            </div>
          )}
      </Card>
    </div>
  );
}
